import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/*
 HelpAssistant
 - Exibe um ícone flutuante (canto inferior esquerdo) em todas as telas (inclui login).
 - Ícone: imagem fornecida em /public (empilhadeira_help.png) ou fallback círculo.
 - Hover: leve escala + tooltip (title nativo evita acessibilidade complexa aqui).
 - Clique: abre balão (card) com resumo + botão "Saiba mais".
 - "Saiba mais": expande texto completo e mostra botão "Voltar" para retornar ao resumo.
 - Botão X fecha assistente (fecha balão mas ícone permanece).
 - Fade in animado ao montar.
*/

// Conteúdos por rota (path prefix). Paths não encontrados usam default.
const HELP_CONTENT = {
  '/login': {
    titulo: 'Ajuda - Login',
    resumo: 'Informe seu email corporativo e senha. Caso não possua acesso solicite ao RH / Gerente. Após autenticar você será redirecionado ao Dashboard.',
    completo: 'Tela de Login:\n1. Preencha email e senha cadastrados.\n2. Clique em Entrar.\n3. Erros de credenciais exibem mensagem em vermelho.\n4. Se esqueceu a senha, solicite redefinição ao RH (fluxo de recuperação poderá ser adicionado futuramente).',
  },
  '/': {
    titulo: 'Ajuda - Dashboard',
    resumo: 'Visão geral: apresenta indicadores de progresso mensal e atalhos. Use o menu lateral para navegar para Métricas, Relatórios e outras seções permitidas ao seu perfil.',
    completo: 'Dashboard:\n- Mostra KPIs consolidados (pontos acumulados, valor estimado de bônus).\n- Utilize este painel para acompanhar rapidamente seu desempenho no mês.\n- A atualização é em tempo quase real após salvar registros de métricas.',
  },
  '/metricas': {
    titulo: 'Ajuda - Métricas Diárias',
    resumo: 'Registre ou visualize métricas do dia. Cada uma das 4 flags vale 1 ponto. O valor diário é proporcional aos pontos obtidos.',
    completo: 'Métricas:\n1. Só é permitido registrar o dia corrente (exceto Líder/RH/Gerente que podem ajustar dias passados).\n2. Cada métrica M1..M4 é binária (0/1).\n3. Total de pontos = soma das flags (0 a 4).\n4. Valor diário = (total/4) * (bônus mensal / dias úteis).\n5. Um registro por colaborador por dia.\n6. Após fechamento do mês os registros ficam consolidados.',
  },
  '/colaboradores': {
    titulo: 'Ajuda - Colaboradores',
    resumo: 'Lista colaboradores para consulta de cargo, setor e bônus padrão. Perfis LIDER/RH/GERENTE podem visualizar e usar dados para registro de métricas.',
    completo: 'Colaboradores:\n- Utilize filtros (quando disponíveis) para localizar rapidamente.\n- As informações auxiliam na escolha correta ao lançar métricas.\n- Mudanças de cargo/setor afetam relatórios segmentados.',
  },
  '/usuarios': {
    titulo: 'Ajuda - Usuários',
    resumo: 'Gestão de contas (RH/Gerente). Crie e ajuste perfis de acesso e status (ativo).',
    completo: 'Usuários:\n1. Somente RH/Gerente criam usuários.\n2. Defina perfil adequado (COLABORADOR, LIDER, RH, GERENTE).\n3. Desativar (ativo = false) impede login sem remover histórico.\n4. Ajuste de perfil repercute imediatamente nos acessos.',
  },
  '/relatorios': {
    titulo: 'Ajuda - Relatórios',
    resumo: 'Gere relatórios por período/setor/cargo. Pode exportar em Excel ou PDF.',
    completo: 'Relatórios:\n- Utilize filtros para delimitar intervalo de datas.\n- Exportações: XLSX (abre em planilha) e PDF para distribuição.\n- Performance: evitar intervalos muito extensos sem necessidade.\n- Ícones de exportação aparecem após carregamento dos dados.',
  },
  '/fechamento': {
    titulo: 'Ajuda - Fechamento Mensal',
    resumo: 'Permite forçar ou visualizar status de fechamento (RH/Gerente). Fechamento normal ocorre automaticamente no 1º dia útil.',
    completo: 'Fechamento:\n1. Processo consolida registros do mês anterior.\n2. Pós-fechamento impede alterações de métricas comuns.\n3. Só force manualmente em caso de contingência.\n4. Registro é auditado (ação CLOSE_MONTH).',
  },
  'default': {
    titulo: 'Ajuda',
    resumo: 'Use este sistema para registrar e acompanhar métricas diárias vinculadas ao bônus mensal.',
    completo: 'Ajuda Geral:\n- Navegue pelo menu lateral.\n- Registre métricas diariamente dentro do prazo.\n- Gere relatórios para análise mensal.\n- Em caso de dúvidas contate seu líder ou RH.',
  }
};

export function HelpAssistant({ imageSrc = '/empilhadeira_help.png' }) {
  const loc = useLocation();
  const [open, setOpen] = useState(false); // balão aberto
  const [expanded, setExpanded] = useState(false); // texto completo
  const [visible, setVisible] = useState(false); // para animar fade-in

  // Detecta caminho base (primeiro segmento) para mapear conteúdo
  const key = useMemo(() => {
    if (loc.pathname === '/login') return '/login';
    if (loc.pathname === '/') return '/';
    const seg = '/' + (loc.pathname.split('/')[1] || '');
    return HELP_CONTENT[seg] ? seg : 'default';
  }, [loc.pathname]);

  const content = HELP_CONTENT[key] || HELP_CONTENT.default;

  useEffect(() => {
    setVisible(true); // ativa fade quando componente montar
    setOpen(false); // fecha ao trocar rota
    setExpanded(false);
  }, [key]);

  return (
    <div className={"help-assistant-root" + (visible ? ' visible':'')} aria-live="polite">
      <button
        className={"help-fab" + (open ? ' active':'')}
        onClick={() => setOpen(o=>!o)}
        title="Ajuda do Sistema"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <img src={imageSrc} alt="Ajuda" className="help-fab-img" onError={e=>{e.currentTarget.style.display='none';}} />
      </button>
      {open && (
        <div className="help-pop" role="dialog" aria-modal="false">
          <div className="help-pop-header">
            <strong>{content.titulo}</strong>
            <button className="help-close" aria-label="Fechar ajuda" onClick={()=> setOpen(false)}>×</button>
          </div>
          <div className="help-pop-body">
            {!expanded ? (
              <>
                <p>{content.resumo}</p>
                <div className="help-actions">
                  <button className="btn outline" onClick={()=> setExpanded(true)}>Saiba mais</button>
                </div>
              </>
            ) : (
              <>
                {content.completo.split('\n').map((l,i)=>(<p key={i}>{l}</p>))}
                <div className="help-actions-row">
                  <button className="btn outline" onClick={()=> setExpanded(false)}>Voltar</button>
                  <button className="btn" onClick={()=> setOpen(false)}>Fechar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
